import { BuildingComplex, Ellipsis, MapIcon } from 'lucide-react';
import { FilterType } from 'bindings/filters/FilterType';
import { ValueKey } from 'bindings/reports/generic/ValueKey';
import { Granularity } from 'bindings/time/Granularity';
import equal from 'deep-equal';
import { ITag } from 'proto/protobufs';
import * as React from 'react';
import ReactDOM from 'react-dom';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import { useRecoilState, useRecoilValue } from 'recoil';
import { AdditionalColumn, additionalColumnTypeToFilterTypeAndPath, AVisibleAdditionalColumns, getFirstValueNotPieChart, ReportColumnType } from '../../atoms/additionalColumns';
import { AdditionalFieldColumn, AVisibleAdditionalFieldColumns, fieldTypeToFilterType } from '../../atoms/additionalFieldColumns';
import { AVisibleCalculatedFields, CalculatedField } from '../../atoms/calculatedFields';
export { generatePromotionFilters } from '../../components_v2/filter/promotionFilters';
import { generatePromotionFilters } from '../../components_v2/filter/promotionFilters';
import { AFrequencyEventType } from '../../atoms/global/frequency';
import { AGranularity } from '../../atoms/global/granularity';
import { ASelectedExternalMappingId, AtomSelectedExternalMappingSelector } from '../../atoms/global/mappings';
import { ASelectedFormId } from '../../atoms/global/selectedForm';
import { AViews, CompanyView, DbView, deleteView, getViews, NewView, postView, putView, ViewCompanyColumns } from '../../atoms/global/views';
import Dropdown from '../../components_v2/dropdown/Dropdown';
import { DropdownData } from '../../components_v2/dropdown/model/Model';
import AdvancedFilters from '../../components_v2/filter/AdvancedFilters';
import { FilterId, FilterResult, FilterTree, filterTreeLength, isLastFormDateFilter } from '../../components_v2/filter/model/Model';
import { convertFilters, FilterParameter } from '../../components_v2/filter/pages/FilterList';
import InputSearch from '../../components_v2/input/InputSearch';
import { ButtonStyle } from '../../components_v2/popup/PopupCreation';
import PopupDeletion from '../../components_v2/popup/PopupDeletion';
import { OrderBy, removeValueFromOrderBy } from '../../components_v2/sort/TableSort';
import { getTags } from '../../components_v2/toolbarFilter/tags/action';
import { TagType } from '../../components_v2/toolbarFilter/tags/Model';
import ToolbarFilterButton from '../../components_v2/toolbarFilter/ToolbarButton';
import PopupView, { ViewCreation } from '../../components_v2/view/PopupView';
import ViewPicker from '../../components_v2/view/ViewPicker';
import { GreySidely3 } from '../../styles/global/css/Utils';
import { Translate, translateToString } from '../../styles/global/translate';
import { useFunctionState } from '../../utils/customHooks';
import { AlertRes } from '../alert/AlertProvider';
import useAlert from '../alert/UseAlert';
import { ToolbarBox, ToolbarImageContainer } from '../globals/defaultToolbar/style/Style';
import { ComponentProps, ToolBarStateWithLeftAndRight } from '../globals/mainPage/mainPage';
import { getClientStatuses } from '../import/actions';
import MapView, { FetchKey } from '../map/MapView';
import { ModalLeftToggle } from '../map/modalLeft/ModalLeftFilter';
import PageSwitch from '../map/PageSwitch';
import { TypeFile } from '../orders/model/Model';
import PermissionContext from '../permissions/PermissionContext';
import Restricted from '../permissions/Restricted';
import { ModalState } from '../products/model';
import Companies, { additionalColumnIdFormatter, additionalFieldColumnIdFormatter, allCompaniesAndAdditionalColumns, calculatedFieldIdFormatter, ColumnResume, COMPANY_COLUMN_CATEGORIES_MAP, CompanySelection } from './Companies';
import { exportCompanies, exportData, getCompanyFilters } from './data/action';
import { filterIdToName } from './data/CompanyColumns';
import { CompanyParams, CompanyStatus } from './model/Model';
import { Column, ColumnOrganizerWrapper } from './popup/ColumnOrganizer';
import CompanyListingPopup from './popup/CompanyListingPopup';
import { SidebarButton } from './style/Style';
import { Cookies } from 'react-cookie';


export const DEFAULT_FILTER_RESULT: FilterResult = {
	values: {
		array: [],
		combinator: 'and'
	},
	formatted: undefined
};

type Pathname = '/companies' | '/map'

export async function generateFormFilters(
	additionalColumns: AdditionalColumn[],
	additionalFieldColumns: AdditionalFieldColumn[],
	calculatedFields: CalculatedField[],
): Promise<{ filterIds: { id: FilterId, name: string }[], filters: FilterParameter[] }> {
	const allFirstValues = await getFirstValueNotPieChart();
	const calculatedFirstValues = allFirstValues.filter(fv => fv.column_type === 'calculated_field');
	const filters: FilterParameter[] = [];
	filters.push(
		additionalColumns.reduce((acc: FilterParameter, x) => {
			if (x.type === 'ReportColumn') return acc;
			const { type, path } = additionalColumnTypeToFilterTypeAndPath(x.type);
			acc.filters.push({
				id: { additional_columns: [{ id: x.id, path: path ?? null, name: false }] },
				name: x.name,
				type
			});
			return acc;
		}, { category: translateToString('additional_columns'), filters: [] })
	);
	filters.push(
		additionalFieldColumns.reduce((acc: FilterParameter, x) => {
			acc.filters.push({
				id: { additional_field_columns: [x.field_id] },
				name: x.field_name,
				type: fieldTypeToFilterType(x.field_type)
			});
			return acc;
		}, { category: translateToString('additional_field_columns'), filters: [] }));

	filters.push(calculatedFields.reduce((acc: FilterParameter, x) => {
		let type: FilterType;
		let path: ValueKey | undefined = undefined;
		let products = false;
		let firstValue = calculatedFirstValues.find(fv => fv.id === x.id);
		if (firstValue?.value && 'products' in firstValue.value) {
			// per-product field: the filter applies to one product's entry, so the
			// type and path come from the entries and the editor adds a product pick
			products = true;
			const inner = ((firstValue.value as unknown as { products: [string, ReportColumnType | string][] }).products ?? [])
				.map(([, value]) => value)
				.find((value): value is ReportColumnType & object => !!value && typeof value === 'object');
			firstValue = inner ? { ...firstValue, value: inner } : undefined;
		}
		if (!firstValue || !firstValue.value) {
			type = 'nullable';
		} else if ('int' in firstValue.value || 'float' in firstValue.value) {
			type = 'numeric';
		} else if ('percentage' in firstValue.value || 'evolution' in firstValue.value) {
			type = 'percentage';
		} else if ('key' in firstValue.value || 'text' in firstValue.value) {
			type = 'string';
		} else {
			type = 'nullable';
		}
		if (firstValue && firstValue.value) {
			path = Object.keys(firstValue.value)[0] as ValueKey;
		}
		acc.filters.push({
			id: { calculated_field_columns: [{ id: x.id, path: path ?? null, name: false }] },
			name: x.field_name,
			type,
			products: products || undefined
		});
		return acc;
	}, { category: translateToString('general.calculated_fields'), filters: [] }));

	return {
		filters,
		filterIds: [
			...additionalColumns.map(ac => ({ id: { additional_columns: [{ id: ac.id, name: false, path: null }] }, name: additionalColumnIdFormatter(ac.id) })),
			...additionalFieldColumns.map(ac => ({ id: { additional_field_columns: [ac.field_id] }, name: additionalFieldColumnIdFormatter(ac.field_id) })),
			...calculatedFields.map<{ id: FilterId, name: string }>(cf => ({ id: { calculated_field_columns: [{ id: cf.id, name: false, path: null }] }, name: calculatedFieldIdFormatter(cf.id) }))
		]
	};

}

export default function CompaniesAndMapView(props: ComponentProps & {
	listingOnly?: boolean,
	height: string,
	companies?: number[],
	setBody?: (body: CompanyParams) => void,
	onSelectionChange?: (selection: CompanySelection) => void
}) {
	const history = useHistory();
	const [isMapModalOpen, setIsMapModalOpen] = React.useState<boolean>(true);
	const [selectedPath, setSelectedPath] = useFunctionState<Pathname>(history.location.pathname, ({ newValue }) => {
		window.history.replaceState(null, '', newValue);
		return newValue;
	});

	const ref = React.useRef<{ hasFiltersOn?:() => boolean }>();
	const [childtoolBarState, setChildToolBarState] = React.useState<ToolBarStateWithLeftAndRight>();

	// Views
	const [views, setViews] = useRecoilState(AViews);
	const [isViewOpen, setIsViewOpen] = React.useState<boolean>(false);
	const [deletionModalState, setDeletionModalState] = React.useState<ModalState<DbView<CompanyView>>>({ isOpen: false });
	const [selectedView, setSelectedView] = useFunctionState<DbView<CompanyView> | undefined>(views.companies?.find(v => v.id.toString() === localStorage.getItem('company_view_id')),
		({ newValue }) => {
			if (newValue) {
				localStorage.setItem('company_view_id', newValue.id.toString());
			} else {
				localStorage.removeItem('company_view_id');
			}
			return newValue;
		}
	);
	const [editedView, setEditedView] = React.useState<DbView<CompanyView> | undefined>();

	// Filters
	const [filterParams, setFilterParams] = React.useState<FilterParameter[]>();
	const [filterResult, setFilterResult] = React.useState<FilterResult | undefined>(selectedView?.data.filters);
	const [isOpenSummary, setOpenSummary] = React.useState<boolean>(false);
	const [isFilterOpen, setFilterOpen] = React.useState<boolean>(false);
	const [selectedFilter, setSelectedFilter] = React.useState<FilterId>();
	const [allColumns, setAllColumns] = React.useState<{ id: FilterId, name: string }[]>([]);

	// Columns
	const calculatedFields = useRecoilValue(AVisibleCalculatedFields);
	const additionalColumns = useRecoilValue(AVisibleAdditionalColumns);
	const additionalFieldColumns = useRecoilValue(AVisibleAdditionalFieldColumns);
	const allCompaniesColumnsAndAdditionalColumns: ColumnResume[] = React.useMemo(() => allCompaniesAndAdditionalColumns(additionalColumns, additionalFieldColumns, calculatedFields), [additionalColumns, additionalFieldColumns, calculatedFields]);
	const companyId = React.useMemo(() => {
		const cookies = new Cookies();
		return cookies.get('company_id');
	}, []);
	const allLocalColumns: Record<string, ViewCompanyColumns[]> = JSON.parse(localStorage.getItem('companies_hidden_columns') || '{}');
	const localColumns: ViewCompanyColumns[] = Array.isArray(allLocalColumns[companyId]) ? allLocalColumns[companyId] : [];
	const baseHiddenColumns = localColumns.length > 0 ? localColumns.filter(l => l.hidden).map(l => l.column) : allCompaniesColumnsAndAdditionalColumns.filter(c => c.hidded).map(c => c.name);
	const baseSortColumns = localColumns.length > 0 ? localColumns.map(l => l.column) : allCompaniesColumnsAndAdditionalColumns.map(c => c.name);
	const defaultVisibleColumnNames = ['select', 'quick_actions', 'name', 'email', 'phone', 'website', 'company_address', 'company_city', 'company_post_code', 'company_country', 'status_id', 'owner_id'];
	const defaultVisibleColumns = allCompaniesColumnsAndAdditionalColumns.map(l => defaultVisibleColumnNames.includes(l.name) ? { column: l.name, hidden: false } : { column: l.name, hidden: true });
	const allDefaultFlags: Record<string, string> = JSON.parse(localStorage.getItem('companies_is_default_hidden_columns') || '{}');
	const isDefaultVisible = !(companyId in allDefaultFlags);
	const [hiddenColumns, setHiddenColumns] = React.useState<string[]>(isDefaultVisible ?
		defaultVisibleColumns.reduce<string[]>((acc, col) => {
			if (col.hidden) {
				acc.push(col.column);
			}
			return acc;
		}, []) : baseHiddenColumns);
	const [sortColumns, setSortColumns] = React.useState<string[]>(baseSortColumns);
	const [hidableColumns, setHidableColumns] = React.useState<Column[]>();

	// Other data
	const [statuses, setStatuses] = React.useState<CompanyStatus[]>([]);
	const [tags, setTags] = React.useState<ITag[]>([]);
	const [searchName, setSearchName] = React.useState<string>();
	const [tabType, setTabType] = React.useState<FetchKey>(selectedView?.data.mapTabType ?? 'status');
	const alert = useAlert();
	const { isAllowedTo } = React.useContext(PermissionContext);
	const dispatch = useDispatch();
	const selectedFormId = useRecoilValue(ASelectedFormId);
	const selectedColumnEventType = useRecoilValue(AFrequencyEventType);
	const selectedExternalMappingId = useRecoilValue(ASelectedExternalMappingId);
	const selectedColumnFormId = useRecoilValue(ASelectedFormId);
	const granularity = useRecoilValue(AGranularity);
	const orderBysHook = React.useState<OrderBy<typeof allColumns[number]>[]>([]);
	const [orderBys, setOrderBys] = orderBysHook;

	const companyListingPopupOpenState = React.useState({ isOpen: false });


	React.useEffect(() => {
		const view = views.companies?.find(v => v.id.toString() === localStorage.getItem('company_view_id'));
		setSelectedView(view);
		if (view) {
			setFilterResult(view.data.filters);
			if (view.data.mapTabType) {
				setTabType(view.data.mapTabType);
			}
			const allStoredCols: Record<string, ViewCompanyColumns[]> = JSON.parse(localStorage.getItem('companies_hidden_columns') || '{}');
			const storedCols = Array.isArray(allStoredCols[companyId]) ? allStoredCols[companyId] : [];
			const columns = view?.data.columns ?? (isDefaultVisible ? defaultVisibleColumns : storedCols);
			if (columns) {
				setHiddenColumns(columns.reduce<string[]>((acc, col) => {
					if (col.hidden) {
						acc.push(col.column);
					}
					return acc;
				}, []));
				setSortColumns(columns.map(l => l.column));
			}
		}
	}, [views]);

	const deleteLocalView = (view: DbView<CompanyView>) => {
		if (view) {
			setDeletionModalState({ isOpen: true, data: view });
		}
	};

	React.useEffect(() => {
		getCompanyFilters().then(async response => {
			let newFilterParams = [...response.filters];
			let newAllColumns = [...response.columns.map(id => ({ id, name: filterIdToName(id) }))];
			if (isAllowedTo({ objectAction: 'ReadForm' })) {
				const { filters, filterIds } = await generateFormFilters(additionalColumns, additionalFieldColumns, calculatedFields);
				newFilterParams = [...newFilterParams, ...filters];
				newAllColumns = [...newAllColumns, ...filterIds];
			}
			if (isAllowedTo({ objectAction: 'ReadCampaign' })) {
				newFilterParams = [...newFilterParams, await generatePromotionFilters()];
			}
			setFilterParams(newFilterParams);
			setAllColumns(newAllColumns);
		});
		getClientStatuses().then(response => {
			setStatuses(response.data);
		});

		getTags(TagType.COMPANY).then(setTags);
	}, []);

	const columnsToView = (): ViewCompanyColumns[] => sortColumns.map(c => ({
		column: c,
		hidden: hiddenColumns.includes(c)
	}));

	const saveView = React.useCallback(async(v: ViewCreation) => {
		const data: NewView<'companies'> = {
			data: {
				filters: filterResult ?? DEFAULT_FILTER_RESULT,
				columns: columnsToView(),
				name: v.name,
				mapTabType: tabType,
				type: 'companies',
				orderBys: orderBys?.map(removeValueFromOrderBy),
			},
			public: v.public,
		};
		await (v.id ? putView(v.id, data) : postView(data));
		getViews().then(newViews => {
			setSelectedView(newViews.companies.find(nv => v.id === nv.id || !views.companies.find(v => v.id == nv.id)));
			setViews(newViews);
		});
	}, [views, filterResult, hiddenColumns, sortColumns, tabType, defaultVisibleColumns, orderBys]);

	const LocalExportCompanies = () => {
		dispatch({ type: 'INSERT_MESSAGE', value: { message: { text: 'exports.ready_toast' }, state: 'loading' } });
		let new_filters: FilterTree | undefined;
		if (selectedPath === '/map') {
			new_filters = {
				and: [{
					val: {
						column: 'latitude',
						operator: 'not_empty',
					}
				},
				{
					val: {
						column: 'longitude',
						operator: 'not_empty',
					}
				}],
			};
			if (filterResult?.formatted) {
				new_filters.and.push(filterResult.formatted);
			}
		} else {
			new_filters = filterResult?.formatted;
		}
		const columns = allColumns.filter(({ name }) => !hiddenColumns.includes(name)).reduce(mapColumnFilterIds(selectedColumnEventType, selectedExternalMappingId, granularity, selectedColumnFormId), []);
		exportCompanies({
			new_filters,
			search: searchName,
			columns
		});
	};

	React.useEffect(() => {
		const dropdownData: Array<DropdownData<string>> = [
			{ label: translateToString(selectedPath === '/companies' ? 'company.export' : 'company.export_map'), value: 'EXPORT' }
		];
		if (selectedPath === '/companies' && isAllowedTo({ objectAction: 'ReadCompanyNote' })) {
			dropdownData.push({ label: translateToString('exports.export_notes'), value: 'EXPORT_NOTES' });
		}
		if (selectedPath === '/companies' && isAllowedTo({ objectAction: 'CreateImport' })) {
			dropdownData.push({ label: translateToString('general.import'), value: 'IMPORT' });
		}
		props.setToolBarState({
			...childtoolBarState,
			bottomLeftToolbarComponent: <ToolbarBox>
				{selectedPath === '/map' && <ModalLeftToggle isOpen={isMapModalOpen} onClick={() => setIsMapModalOpen(open => !open)} />}
				{childtoolBarState?.leftBottomLeft}
				<ViewPicker
					type='companies'
					noBorder
					selectedView={selectedView}
					views={views.companies}
					onViewChange={view => {
						setSelectedView(view);
						let filters: FilterResult;
						if (view) {
							filters = { ...view.data.filters, formatted: convertFilters(view.data.filters.values) };
							if (view.data.mapTabType) {
								setTabType(view.data.mapTabType);
							}
						} else {
							filters = DEFAULT_FILTER_RESULT;
						}
						setFilterResult(filters);
						const allStoredCols: Record<string, ViewCompanyColumns[]> = JSON.parse(localStorage.getItem('companies_hidden_columns') || '{}');
						const storedCols = Array.isArray(allStoredCols[companyId]) ? allStoredCols[companyId] : [];
						const columns = view?.data.columns ?? (isDefaultVisible ? defaultVisibleColumns : storedCols);
						if (columns) {
							setHiddenColumns(columns.reduce((acc, col) => {
								if (col.hidden) {
									acc.push(col.column);
								}
								return acc;
							}, []));
							setSortColumns(columns.map(l => l.column));
						}
						setOrderBys(view?.data.orderBys ?? []);
					}}
					onDelete={deleteLocalView}
					onCreate={() => { setSelectedView(undefined); setIsViewOpen(true); }}
					onEdit={() => setIsViewOpen(true)}
					setEditedView={setEditedView}
				/>
				<ToolbarFilterButton
					activeFilters={filterTreeLength(filterResult?.formatted)}
					onClick={() => {
						setOpenSummary(true);
						setFilterOpen(true);
					}}
					onDeleteFilter={() => {
						setFilterResult({ values: { combinator: 'and', array: [] }, formatted: undefined });
					}}
				/>
				{childtoolBarState?.beforeSaveView}
				{(!equal(filterResult?.formatted, selectedView?.data.filters.formatted ?? DEFAULT_FILTER_RESULT.formatted)
					|| !equal(hiddenColumns, selectedView?.data.columns?.filter(l => l.hidden).map(l => l.column) ?? baseHiddenColumns)
					|| !equal(sortColumns, selectedView?.data.columns?.map(l => l.column) ?? baseSortColumns)
					|| !equal(tabType, selectedView?.data.mapTabType ?? 'status')
					|| !equal(orderBys?.map(removeValueFromOrderBy), selectedView?.data.orderBys ?? [])
				) && <SidebarButton onClick={() => {
					if (ref?.current?.hasFiltersOn?.()) {
						alert({
							title: translateToString('map_datas'),
							content: <div><Translate id='map_datas_not_on_view' /></div>,
							gap: '0px',
							buttons: [{ title: translateToString('continue'), res: AlertRes.Ok, style: ButtonStyle.White }]
						}).then(res => {
							if (res.res === AlertRes.Ok) {
								setIsViewOpen(true);
							}
						});
					} else {
						setIsViewOpen(true);
					}
				}}><Translate id='save_view' /></SidebarButton>}
				{childtoolBarState?.rightBottomLeft}
			</ToolbarBox>,
			bottomRightToolbarComponent: <ToolbarBox>
				{childtoolBarState?.leftBottomRight}
				{selectedPath === '/companies' &&
					<ColumnOrganizerWrapper
						categories={Object.keys(COMPANY_COLUMN_CATEGORIES_MAP)}
						columns={hidableColumns ?? []}
						onSort={setSortColumns}
						setHiddenColumns={(columns) => {
							const flags: Record<string, string> = JSON.parse(localStorage.getItem('companies_is_default_hidden_columns') || '{}');
							flags[companyId] = 'false';
							localStorage.setItem('companies_is_default_hidden_columns', JSON.stringify(flags));
							setHiddenColumns(columns);
						}}
						longIcon
					/>
				}
				<Restricted to={{ objectAction: 'ViewMap' }}>
					<Restricted to={{ objectAction: 'ViewCompany' }}>
						<PageSwitch<Pathname> selectedValue={selectedPath} background={true} list={[{
							value: '/companies', img: <BuildingComplex />, onClick: (path) => {
								if (ref?.current?.hasFiltersOn?.()) {
									alert({
										title: translateToString('map_datas'),
										content: <div><Translate id='map_datas_are_on' /></div>,
										gap: '0px',
										buttons: [{ title: translateToString('continue'), res: AlertRes.Ok, style: ButtonStyle.White }]
									}).then(res => {
										if (res.res === AlertRes.Ok) {
											setSelectedPath(path);
										}
									});
								} else {
									setSelectedPath(path);
								}
							}
						}, { value: '/map', img: <MapIcon />, onClick: setSelectedPath }]} />
					</Restricted>
				</Restricted>
				<InputSearch
					placeholder={translateToString('search')}
					name='search_company_name'
					type='text'
					value={searchName}
					onChange={(value) => setSearchName(value)}
					delay={500}
				/>
				{childtoolBarState?.rightBottomRight}
				<Restricted to={{ objectAction: 'CreateExport' }}>
					<Dropdown
						searchable={false}
						dropdownStyle={{ optionLeft: '-115px', optionWidth: '160px' }}
						datalist={dropdownData}
						name='options'
						JSXButton={() => <ToolbarImageContainer hasPointer><Ellipsis size={18} strokeWidth={2.5} color={GreySidely3} /></ToolbarImageContainer>}
						onChange={(value: DropdownData<string>) => {
							switch (value.value) {
								case 'EXPORT': {
									// if (ref?.current?.hasFiltersOn?.()) {
									alert({
										title: translateToString('map_datas'),
										content: <div><Translate id='map_datas_not_on_export' /></div>,
										gap: '0px',
										buttons: [{ title: translateToString('continue'), res: AlertRes.Ok, style: ButtonStyle.White }]
									}).then(res => {
										if (res.res === AlertRes.Ok) {
											LocalExportCompanies();
										}
									});
									// } else {
									// 	LocalExportCompanies();
									// }
									return;
								}
								case 'EXPORT_NOTES': {
									dispatch({ type: 'INSERT_MESSAGE', value: { message: { text: 'exports.ready_toast' }, state: 'loading' } });
									exportData(TypeFile.Excel, { type: 'Notes', kind: 'Companies' });
									return;
								}
								case 'IMPORT': {
									history.push({ pathname: '/settings', hash: 'import' });
									return;
								}
							}
						}}
					/>
				</Restricted>
			</ToolbarBox>
		});
	}, [selectedFormId, views, childtoolBarState, selectedView, filterResult, hiddenColumns, sortColumns, selectedPath, tabType, orderBys, isMapModalOpen]);

	return <>
		{!props.listingOnly && <CompanyListingPopup modalStateHook={companyListingPopupOpenState} order_bys={orderBys} filters={filterResult?.formatted} />}
		{!props.listingOnly && selectedPath === '/map' && <MapView
			setToolBarState={setChildToolBarState}
			isModalOpen={isMapModalOpen}
			setIsModalOpen={setIsMapModalOpen}
			filterResult={filterResult}
			statuses={statuses}
			searchName={searchName}
			ref={ref}
			tabType={tabType}
			setTabType={setTabType}
			tags={tags}
			companyListingPopupOpenState={companyListingPopupOpenState}
		/>}
		{(props.listingOnly || selectedPath === '/companies') && <Companies
			isPopup={props.listingOnly}
			companies={props.companies}
			companyListingPopupOpenState={companyListingPopupOpenState}
			allCompaniesColumnsAndAdditionalColumns={allCompaniesColumnsAndAdditionalColumns}
			setToolBarState={setChildToolBarState}
			filterResult={filterResult ?? DEFAULT_FILTER_RESULT}
			selectedView={selectedView}
			setSelectedFilter={setSelectedFilter}
			setOpenSummary={setOpenSummary}
			setFilterOpen={setFilterOpen}
			setHiddenColumns={setHiddenColumns}
			hiddenColumns={hiddenColumns}
			setSortColumns={setSortColumns}
			sortColumns={sortColumns}
			allColumns={allColumns}
			statuses={statuses}
			tags={tags}
			orderBysHook={orderBysHook}
			searchName={searchName}
			onResetFilters={() => {
				setFilterResult(DEFAULT_FILTER_RESULT);
				setSearchName(undefined);
			}}
			setHidableColumns={setHidableColumns}
			height={props.height}
			setBody={props.setBody}
			onSelectionChange={props.onSelectionChange}
		/>}
		<PopupView
			isOpen={isViewOpen}
			view={editedView ?? selectedView}
			onClickOut={() => { setIsViewOpen(false); setEditedView(undefined); }}
			onValidation={(v) => { saveView(v).then(_ => setIsViewOpen(false)); setEditedView(undefined); }}
		/>
		<PopupDeletion
			records={1}
			translationKey='view'
			noVerification
			isOpen={deletionModalState.isOpen}
			onClickOut={() => setDeletionModalState({ isOpen: false })}
			onValidation={() => {
				if (deletionModalState.data) {
					deleteView(deletionModalState.data.id).then(_ => getViews().then(setViews));
				}
			}}
		/>
		{/* portaled to the body: inside a transformed ancestor (the listing
		embedded in a centered popup) the panel's fixed position would anchor
		to the popup instead of the viewport and render off screen */}
		{ReactDOM.createPortal(<AdvancedFilters
			permission='ReadCompany'
			isOpen={isFilterOpen}
			setOpen={setFilterOpen}
			isOpenSummary={isOpenSummary}
			filterList={filterParams}
			filterValues={filterResult?.values}
			onChange={(value) => setFilterResult(value)}
			selectedFilter={selectedFilter}
		/>, document.body)}
	</>;
}

export const mapColumnFilterIds = (selectedColumnEventType: number, selectedExternalMappingId: AtomSelectedExternalMappingSelector, granularity: Granularity, selectedColumnFormId: number) => (acc: FilterId[], elem: { id: FilterId }): FilterId[] => {
	const { id } = elem;
	if (typeof id === 'string') {
		acc.push(id);
	} else if ('frequencies' in id) {
		if (!acc.some(acc_id => typeof acc_id !== 'string' && 'frequencies' in acc_id)) {
			acc.push({ frequencies: { event: selectedColumnEventType, element: id.frequencies.element } });
		}
	} else if ('last_event_date' in id) acc.push({ last_event_date: selectedColumnEventType });
	else if ('next_event_date' in id) acc.push({ next_event_date: selectedColumnEventType });
	else if ('external_id' in id && selectedExternalMappingId.company) acc.push({ external_id: selectedExternalMappingId.company });
	else if ('checkout' in id) {
		if (!acc.some(acc_id => typeof acc_id !== 'string' && 'checkout' in acc_id)) acc.push({
			checkout: {
				element: 'evolution',
				granularity
			}
		});
	} else if ('additional_columns' in id) {
		const ac = acc.find(ac => typeof ac != 'string' && 'additional_columns' in ac);
		if (ac && typeof ac != 'string' && 'additional_columns' in ac) {
			if (!ac.additional_columns.some(ac => ac.id === id.additional_columns[0].id)) {
				ac.additional_columns.push(id.additional_columns[0]);
			}
		} else acc.push(id);
	} else if ('additional_field_columns' in id) {
		let ac = acc.find(ac => typeof ac != 'string' && 'additional_field_columns' in ac);
		if (!ac) {
			const obj = { additional_field_columns: [] };
			acc.push(obj);
			ac = obj;
		}
		for (const additional_field_column_id of id.additional_field_columns) {
			if (ac.additional_field_columns.some(id => id === additional_field_column_id)) continue;
			ac.additional_field_columns.push(additional_field_column_id);
		}
	} else if (isLastFormDateFilter(id)) acc.push({ last_form_date: selectedColumnFormId });
	else if ('calculated_field_columns' in id) {
		const ac = acc.find(ac => typeof ac != 'string' && 'calculated_field_columns' in ac);
		if (ac && typeof ac != 'string' && 'calculated_field_columns' in ac) {
			if (!ac.calculated_field_columns.some(ac => ac.id === id.calculated_field_columns[0].id)) {
				ac.calculated_field_columns.push(id.calculated_field_columns[0]);
			}
		} else acc.push(id);
	}
	return acc;
};
